import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { useBrandingStore } from '../../stores/branding.store';
import { getAccessTokenExpiry } from '../../lib/api';

// Topbar session-state pill. Same visual language as the role badge it sits
// next to: rounded-full, border, x-small font, py-1 px-2.5. Colors come from
// the existing palette — no hardcoded values outside the design system.
//
// Shows two things side-by-side:
//   - "Connesso 12m" — how long since login (uses auth.store.loginAt, the
//     single source of truth introduced in v9.62)
//   - "Scade 04:32" — minutes:seconds until the inactivity timeout locks the
//     screen (it no longer logs out: the session and the softphone survive)
//     (resets on EVERY meaningful activity: input, route nav, API mutation,
//      active call — see SessionGuard for the canonical list)
//
// On every reset the badge briefly pulses + the countdown number flashes
// green so the user can SEE that their action kept the session alive.
//
// Color escalates as the countdown nears zero:
//   > 5 min → neutral
//   ≤ 5 min → amber
//   ≤ 1 min → red
//
// Hidden when not authenticated; hidden on screens < `sm` to keep the
// mobile topbar uncluttered.

const fmtAge = (sec) => {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
};

const fmtRemaining = (sec) => {
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

export default function SessionTimerBadge() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const loginAt = useAuthStore((s) => s.loginAt);
  const branding = useBrandingStore((s) => s.branding);

  const inactivityMinutes = Math.max(2, Number(branding?.sec_session_inactivity_minutes) || 30);
  const inactivitySeconds = inactivityMinutes * 60;

  const [now, setNow] = useState(Date.now());
  // Use a ref for last-activity so the firehose of mousemove events doesn't
  // cause a re-render per move. The 1 s tick reads the ref and recomputes
  // the displayed remaining time.
  const lastActivityRef = useRef(Date.now());
  const [flashKey, setFlashKey] = useState(0);
  const [callActive, setCallActive] = useState(false);

  // 1-second tick — only state update on the badge per second. Cheap.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [isAuthenticated]);

  // Direct local listeners for the high-frequency input stream — keeps the
  // displayed countdown responsive without going through SessionGuard's 1 s
  // debounce. The badge still listens to wfm-session-reset for the
  // non-input sources (route nav, API mutations, recovery flow).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const evts = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    let lastBumpAt = 0;
    const bump = () => {
      lastActivityRef.current = Date.now();
      // Throttle the visible "flash" to once per second so the badge
      // doesn't strobe under continuous mouse movement.
      if (Date.now() - lastBumpAt > 1000) {
        lastBumpAt = Date.now();
        setFlashKey((k) => k + 1);
      }
    };
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const onReset = () => {
      lastActivityRef.current = Date.now();
      setFlashKey((k) => k + 1);
    };
    window.addEventListener('wfm-session-reset', onReset);

    const onCall = (e) => {
      const active = e?.detail?.active === true;
      setCallActive(active);
      if (active) lastActivityRef.current = Date.now();
    };
    window.addEventListener('wfm-call-state', onCall);

    return () => {
      evts.forEach((e) => window.removeEventListener(e, bump));
      window.removeEventListener('wfm-session-reset', onReset);
      window.removeEventListener('wfm-call-state', onCall);
    };
  }, [isAuthenticated]);

  // Drive the brief reset-flash class. Toggles for 800 ms after each
  // activity / reset event.
  const [flashing, setFlashing] = useState(false);
  useEffect(() => {
    if (flashKey === 0) return undefined;
    setFlashing(true);
    const t = setTimeout(() => setFlashing(false), 800);
    return () => clearTimeout(t);
  }, [flashKey]);

  if (!isAuthenticated || !loginAt) return null;

  const ageSec = Math.max(0, Math.floor((now - loginAt) / 1000));
  const idleSec = Math.floor((now - lastActivityRef.current) / 1000);
  const remainingSec = Math.max(0, inactivitySeconds - idleSec);

  // Access-token expiry — if it's about to lapse AND no refresh has yet
  // bumped it, surface that as the urgent number instead. The proactive
  // refresh normally keeps this comfortably high; this is belt-and-braces
  // for the rare case where the refresh stalled.
  const accExp = getAccessTokenExpiry();
  const accExpRemaining = accExp ? Math.max(0, Math.floor((accExp - now) / 1000)) : null;
  const effectiveRemaining = (accExpRemaining !== null && accExpRemaining < remainingSec)
    ? accExpRemaining
    : remainingSec;

  let remainingColor = 'var(--wfm-text-mid)';      // text-mid
  if (effectiveRemaining <= 60) remainingColor = 'var(--wfm-accent)';        // status danger
  else if (effectiveRemaining <= 5 * 60) remainingColor = '#D97706'; // amber
  if (flashing) remainingColor = '#16A34A'; // success green during reset flash

  // During an active call the countdown is meaningless (we keep the session
  // alive forever for the call). Show an "∞" affordance instead.
  const showInfinity = callActive;

  return (
    <div
      className={`hidden sm:flex items-center gap-2 px-2.5 py-1 text-xs font-semibold rounded-full border bg-bg-white transition-shadow ${flashing ? 'wfm-session-badge-flash' : ''}`}
      style={{
        borderColor: flashing ? '#16A34A' : 'var(--wfm-border)',
        color: 'var(--wfm-text-mid)',
      }}
      title={
        showInfinity
          ? `Chiamata in corso — sessione tenuta attiva. Connesso da ${fmtAge(ageSec)}.`
          : `Sessione iniziata ${ageSec >= 60 ? 'da ' + fmtAge(ageSec) : 'pochi secondi fa'}. Blocco schermo per inattività tra ${fmtRemaining(effectiveRemaining)} se non utilizzi l'app — il telefono resta attivo e continui a ricevere chiamate.`
      }
      aria-label={
        showInfinity
          ? `Chiamata attiva. Sessione tenuta in vita. Connesso da ${fmtAge(ageSec)}.`
          : `Sessione attiva da ${fmtAge(ageSec)}. Blocco schermo tra ${fmtRemaining(effectiveRemaining)} in caso di inattività.`
      }
    >
      <Clock size={14} aria-hidden="true" style={{ color: 'var(--wfm-primary-ink)' }} />
      <span className="tabular-nums" style={{ color: 'var(--wfm-primary-ink)' }}>
        {fmtAge(ageSec)}
      </span>
      <span aria-hidden="true" style={{ color: '#D1D5DB' }}>·</span>
      <span
        className="tabular-nums"
        style={{ color: remainingColor }}
      >
        {showInfinity ? '∞' : fmtRemaining(effectiveRemaining)}
      </span>
      <style>{`
        @keyframes wfm-session-badge-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.35); }
          70%  { box-shadow: 0 0 0 6px rgba(22, 163, 74, 0); }
          100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
        }
        .wfm-session-badge-flash {
          animation: wfm-session-badge-pulse 0.8s ease-out 1;
        }
      `}</style>
    </div>
  );
}
